import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Upload, CheckCircle, AlertCircle } from 'lucide-react';

const ProviderSetup = ({ provider, config, onSave, onTest }) => {
  const [formData, setFormData] = useState({
    providerName: '',
    entityId: '',
    ssoUrl: '',
    metadataUrl: '',
    certificate: ''
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        providerName: config?.provider_name || '',
        entityId: config?.entity_id || '',
        ssoUrl: config?.sso_url || '',
        metadataUrl: config?.metadata_url || '',
        certificate: config?.certificate || ''
      });
    } else {
      // Set default provider name
      const defaultNames = {
        okta: 'Okta Enterprise SSO',
        azure_ad: 'Azure Active Directory',
        google_workspace: 'Google Workspace SSO'
      };
      setFormData(prev => ({
        ...prev,
        providerName: defaultNames?.[provider] || ''
      }));
    }
  }, [config, provider]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCertificateUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, certificate: event?.target?.result }));
      };
      reader?.readAsText(file);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(provider, formData);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await onTest(provider);
    } finally {
      setTesting(false);
    }
  };

  const getProviderInstructions = () => {
    const instructions = {
      okta: {
        title: 'Okta Setup Instructions',
        steps: [
          'Log in to your Okta Admin Console',
          'Navigate to Applications > Applications',
          'Click Create App Integration and select SAML 2.0',
          'Configure Single Sign-On URL and Audience URI',
          'Download the SAML metadata or copy the certificate',
          'Paste the configuration details below'
        ]
      },
      azure_ad: {
        title: 'Azure AD Setup Instructions',
        steps: [
          'Sign in to Azure Portal',
          'Navigate to Azure Active Directory > Enterprise Applications',
          'Click New Application > Create your own application',
          'Select Integrate any other application (Non-gallery)',
          'Configure SAML-based sign-on',
          'Copy the Login URL, Azure AD Identifier, and Certificate'
        ]
      },
      google_workspace: {
        title: 'Google Workspace Setup Instructions',
        steps: [
          'Sign in to Google Admin Console',
          'Navigate to Apps > Web and mobile apps',
          'Click Add App > Add custom SAML app',
          'Download IdP metadata or copy SSO URL and Certificate',
          'Configure Service Provider Details',
          'Paste the configuration details below'
        ]
      }
    };
    return instructions?.[provider] || instructions?.okta;
  };

  const instructions = getProviderInstructions();

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">{instructions?.title}</h3>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          {instructions?.steps?.map((step, index) => (
            <li key={index}>{step}</li>
          ))}
        </ol>
      </div>

      {/* Configuration Form */}
      <div className="space-y-4">
        <Input
          label="Provider Name"
          placeholder="e.g., Okta Enterprise SSO"
          value={formData?.providerName}
          onChange={(e) => handleInputChange('providerName', e?.target?.value)}
          description="Friendly name for this SSO provider"
        />

        <Input
          label="Entity ID / Issuer"
          placeholder="https://your-domain.okta.com"
          value={formData?.entityId}
          onChange={(e) => handleInputChange('entityId', e?.target?.value)}
          description="Identity provider entity ID or issuer URL"
        />

        <Input
          label="SSO URL / Login URL"
          placeholder="https://your-domain.okta.com/app/app_id/sso/saml"
          value={formData?.ssoUrl}
          onChange={(e) => handleInputChange('ssoUrl', e?.target?.value)}
          description="SAML single sign-on endpoint URL"
        />

        <Input
          label="Metadata URL (Optional)"
          placeholder="https://your-domain.okta.com/app/app_id/sso/saml/metadata"
          value={formData?.metadataUrl}
          onChange={(e) => handleInputChange('metadataUrl', e?.target?.value)}
          description="URL to fetch SAML metadata automatically"
        />

        {/* Certificate Upload */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            X.509 Certificate
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors">
            <input
              type="file"
              accept=".cer,.crt,.pem"
              onChange={handleCertificateUpload}
              className="hidden"
              id="certificate-upload"
            />
            <label htmlFor="certificate-upload" className="cursor-pointer">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-1">
                Click to upload certificate or paste below
              </p>
              <p className="text-xs text-muted-foreground">
                Supports .cer, .crt, .pem formats
              </p>
            </label>
          </div>
          <textarea
            placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDXTCCAkWgAwIBAgIJAKL...&#10;-----END CERTIFICATE-----"
            value={formData?.certificate}
            onChange={(e) => handleInputChange('certificate', e?.target?.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-xs"
          />
          <p className="text-xs text-muted-foreground">
            Paste the X.509 certificate from your identity provider
          </p>
        </div>
      </div>

      {/* Connection Status */}
      {config && (
        <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg">
          {config?.connection_status === 'active' && (
            <>
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm text-green-700 font-medium">Connection Active</span>
            </>
          )}
          {config?.connection_status === 'testing' && (
            <>
              <AlertCircle className="w-5 h-5 text-amber-500" />
              <span className="text-sm text-amber-700 font-medium">Testing Configuration</span>
            </>
          )}
          {config?.connection_status === 'inactive' && (
            <>
              <AlertCircle className="w-5 h-5 text-gray-500" />
              <span className="text-sm text-gray-700 font-medium">Connection Inactive</span>
            </>
          )}
          {config?.connection_status === 'error' && (
            <>
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-red-700 font-medium">Connection Error</span>
            </>
          )}
          {config?.last_test_at && (
            <span className="text-xs text-muted-foreground ml-auto">
              Last tested: {new Date(config?.last_test_at)?.toLocaleString()}
            </span>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={handleSave}
          loading={loading}
          disabled={loading || !formData?.providerName || !formData?.ssoUrl}
          className="flex-1"
        >
          {config ? 'Update Configuration' : 'Save Configuration'}
        </Button>
        {config && (
          <Button
            variant="outline"
            onClick={handleTest}
            loading={testing}
            disabled={testing || loading}
          >
            Test Connection
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProviderSetup;